import { useMemo } from "react";
import { AGING_BUCKETS, summarizeAging } from "../utils/aging";

const money = (n) =>
  (Number(n) || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

// Standard aging summary block — 5 buckets (Current / 1-30 / 31-60 /
// 61-90 / 90+) with $ outstanding + count per bucket. Same shape on AR
// (InvoicesPage) and AP (POsPage) so the bookkeeper sees identical
// reports on both sides.
//
// Props:
//   title — heading (e.g. "AR Aging" or "AP Aging")
//   bills — array of bills with grandTotal, paidAmount, dueDate /
//           finalizedAt. The summarize helper handles void / paid /
//           overdue logic so callers don't repeat themselves.
//   onBucketClick — optional (bucketKey) => void. When provided, each
//                   bucket tile renders as a button that filters the
//                   underlying list. Falls back to non-interactive.
export default function AgingSummary({ title, bills = [], onBucketClick }) {
  const { buckets, totalOutstanding } = useMemo(
    () => summarizeAging(bills),
    [bills]
  );

  if (totalOutstanding < 0.005) return null;

  return (
    <div
      style={{
        marginTop: "12px",
        marginBottom: "12px",
        padding: "10px 12px",
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: "6px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: "8px",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <strong style={{ fontSize: "13px", color: "#374151" }}>
          {title}
        </strong>
        <span style={{ fontSize: "13px", color: "#374151" }}>
          Total outstanding:{" "}
          <strong style={{ color: "#b91c1c" }}>{money(totalOutstanding)}</strong>
        </span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
          gap: "6px",
        }}
      >
        {AGING_BUCKETS.map((bucket) => {
          const data = buckets[bucket.key] || { amount: 0, count: 0 };
          const isClickable = onBucketClick && data.amount > 0;
          const inner = (
            <>
              <div
                style={{
                  fontSize: "11px",
                  color: bucket.color,
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                {bucket.label}
              </div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: bucket.color,
                }}
              >
                {money(data.amount)}
              </div>
              <div style={{ fontSize: "11px", color: "#6b7280" }}>
                {data.count} bill{data.count === 1 ? "" : "s"}
              </div>
            </>
          );
          if (isClickable) {
            return (
              <button
                key={bucket.key}
                onClick={() => onBucketClick(bucket.key)}
                style={{
                  padding: "8px 10px",
                  borderRadius: "4px",
                  background: bucket.bg,
                  border: `1px solid ${bucket.color}40`,
                  cursor: "pointer",
                  textAlign: "left",
                  font: "inherit",
                }}
                title={`Filter list to the ${bucket.label} bucket`}
              >
                {inner}
              </button>
            );
          }
          return (
            <div
              key={bucket.key}
              style={{
                padding: "8px 10px",
                borderRadius: "4px",
                background: bucket.bg,
                border: `1px solid ${bucket.color}40`,
              }}
            >
              {inner}
            </div>
          );
        })}
      </div>
    </div>
  );
}
